/**
 * G9 feedback on the user's device: project feedback sources (sealed `feedbackcfg` role config),
 * the owning daemons' status records, and the review queue of daemon-written `feedback` proposals.
 *
 * Every entity has one writer role. This device writes only config and decision records and — on
 * approval — the task itself (deterministic id, built by the shared quarantining builder). The
 * daemon folds decisions into its proposals; the queue shows a decision as soon as it is written.
 */
import {
  FeedbackCfgEntity,
  type FeedbackConfigRecord,
  FeedbackConfigRecord as FeedbackConfigSchema,
  type FeedbackDecisionRecord,
  FeedbackProposalRecord,
  type FeedbackProposalStatus,
  type FeedbackSource,
  type FeedbackStatusRecord,
  feedbackDecisionId,
  feedbackTaskId,
  feedbackTaskRecord,
} from '@viko/protocol'

type Kind = 'feedbackcfg' | 'feedback' | 'task'
interface Row {
  id: string
  value: Record<string, unknown>
}
/** The slice of BrowserEntities this module needs (sealed, authenticated, E2E). */
export interface FeedbackEntityStore {
  list(kind: Kind): Promise<Row[]>
  read(kind: Kind, id: string): Promise<Row>
  write(kind: Kind, id: string, value: Record<string, unknown>): Promise<unknown>
  create(kind: Kind, value: Record<string, unknown>): Promise<{ id: string }>
}

export interface ProposalView {
  id: string
  p: FeedbackProposalRecord
  decision?: FeedbackDecisionRecord
  /** The decision (if any) wins until the daemon folds it in. */
  status: FeedbackProposalStatus
}

export interface FeedbackState {
  configId?: string
  config: FeedbackConfigRecord
  /** Per machine that owns sources in this project. */
  status: FeedbackStatusRecord[]
  proposals: ProposalView[]
}

export const emptyFeedbackConfig = (projectId: string): FeedbackConfigRecord => ({
  role: 'config',
  projectId,
  sources: [],
  trust: { dev: [], tester: [] },
  autonomy: { dev: 'review', tester: 'review' },
  testRequests: {},
  updatedAt: 0,
})

const DECIDED: Record<FeedbackDecisionRecord['action'], FeedbackProposalStatus> = {
  approve: 'approved',
  reject: 'rejected',
  merge: 'merged',
}

/** Review order: needs-you first, then priority, then most recent. */
export function sortProposals(rows: ProposalView[]): ProposalView[] {
  const rank = (v: ProposalView) => (v.status === 'pending' ? 0 : 1)
  return [...rows].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      b.p.priority - a.p.priority ||
      b.p.lastAt - a.p.lastAt ||
      a.id.localeCompare(b.id),
  )
}

export async function loadFeedback(
  store: FeedbackEntityStore,
  projectId: string,
): Promise<FeedbackState> {
  const [cfgRows, proposalRows] = await Promise.all([
    store.list('feedbackcfg'),
    store.list('feedback'),
  ])
  let config: { id: string; v: FeedbackConfigRecord } | undefined
  const status: FeedbackStatusRecord[] = []
  const decisions = new Map<string, FeedbackDecisionRecord>()
  for (const row of cfgRows) {
    const parsed = FeedbackCfgEntity.safeParse(row.value)
    if (!parsed.success || parsed.data.projectId !== projectId) continue
    const v = parsed.data
    if (v.role === 'config') {
      // One config per project; if two devices raced, the newest wins deterministically.
      if (!config || v.updatedAt > config.v.updatedAt) config = { id: row.id, v }
    } else if (v.role === 'status') status.push(v)
    else if (row.id === feedbackDecisionId(v.proposalId)) decisions.set(v.proposalId, v)
  }
  const proposals: ProposalView[] = []
  for (const row of proposalRows) {
    const parsed = FeedbackProposalRecord.safeParse(row.value)
    if (!parsed.success || parsed.data.projectId !== projectId) continue
    const p = parsed.data
    const decision = decisions.get(row.id)
    proposals.push({
      id: row.id,
      p,
      ...(decision ? { decision } : {}),
      status: p.status === 'pending' && decision ? DECIDED[decision.action] : p.status,
    })
  }
  return {
    ...(config ? { configId: config.id } : {}),
    config: config?.v ?? emptyFeedbackConfig(projectId),
    status,
    proposals: sortProposals(proposals),
  }
}

async function readOrNull(store: FeedbackEntityStore, kind: Kind, id: string) {
  try {
    return await store.read(kind, id)
  } catch {
    return null
  }
}

/** Read-modify-write of the project's config (this device is its only writer role). */
export async function saveFeedbackConfig(
  store: FeedbackEntityStore,
  projectId: string,
  configId: string | undefined,
  mutate: (cfg: FeedbackConfigRecord) => FeedbackConfigRecord,
): Promise<string> {
  const current = configId ? await store.read('feedbackcfg', configId) : null
  const base = current ? FeedbackConfigSchema.parse(current.value) : emptyFeedbackConfig(projectId)
  if (base.projectId !== projectId) throw new Error('Feedback config belongs to another project')
  const next = FeedbackConfigSchema.parse({
    ...mutate(base),
    role: 'config',
    projectId,
    updatedAt: Date.now(),
  })
  if (configId) {
    await store.write('feedbackcfg', configId, next)
    return configId
  }
  return (await store.create('feedbackcfg', next)).id
}

export const upsertSource = (cfg: FeedbackConfigRecord, source: FeedbackSource) => ({
  ...cfg,
  sources: [...cfg.sources.filter((s) => s.id !== source.id), source],
})
export const removeSource = (cfg: FeedbackConfigRecord, sourceId: string) => {
  const { [sourceId]: _t, ...testRequests } = cfg.testRequests
  return { ...cfg, sources: cfg.sources.filter((s) => s.id !== sourceId), testRequests }
}
export const requestTest = (cfg: FeedbackConfigRecord, sourceId: string, now = Date.now()) => ({
  ...cfg,
  testRequests: { ...cfg.testRequests, [sourceId]: now },
})

/** One matcher per line/comma; lowercased; invalid lines are reported, not silently kept. */
export function parseMatchers(text: string): { ok: string[]; bad: string[] } {
  const ok: string[] = []
  const bad: string[] = []
  for (const raw of text.split(/[\n,]/)) {
    const m = raw.trim().toLowerCase()
    if (!m) continue
    if (/^(@[a-z0-9.-]+\.[a-z]{2,}|[^\s@]+@[a-z0-9.-]+\.[a-z]{2,}|[a-z0-9:_.-]{2,})$/.test(m))
      ok.includes(m) || ok.push(m)
    else bad.push(raw.trim())
  }
  return { ok, bad }
}

async function writeDecision(
  store: FeedbackEntityStore,
  d: Omit<FeedbackDecisionRecord, 'role' | 'at'>,
) {
  const id = feedbackDecisionId(d.proposalId)
  // Sync this device's checkpoint first so the write chains onto the latest revision.
  await readOrNull(store, 'feedbackcfg', id)
  await store.write('feedbackcfg', id, { role: 'decision', ...d, at: Date.now() })
}

export type FeedbackAction = 'approve' | 'reject' | 'merge'

/**
 * Batch decisions. Only proposals still pending (and in this project) are decided. Approval
 * creates the task on this device with the shared builder (quarantined brief, no scope widening).
 * Merge folds every other selected proposal into the first (highest priority) one.
 */
export async function decideFeedback(
  store: FeedbackEntityStore,
  projectId: string,
  selected: ProposalView[],
  action: FeedbackAction,
): Promise<{ decided: string[]; skipped: string[] }> {
  const decided: string[] = []
  const skipped: string[] = []
  const pending = sortProposals(selected).filter((v) => {
    const ok = v.status === 'pending' && v.p.projectId === projectId
    if (!ok) skipped.push(v.id)
    return ok
  })
  if (action === 'merge') {
    const [target, ...rest] = pending
    if (!target || !rest.length) throw new Error('Select at least two pending proposals to merge')
    for (const v of rest) {
      await writeDecision(store, {
        projectId,
        proposalId: v.id,
        action: 'merge',
        mergeInto: target.id,
      })
      decided.push(v.id)
    }
    return { decided, skipped: [...skipped, target.id] }
  }
  for (const v of pending) {
    if (action === 'approve') {
      // Re-read: approve exactly what the daemon holds now, not a stale view.
      const fresh = FeedbackProposalRecord.parse((await store.read('feedback', v.id)).value)
      if (fresh.status !== 'pending' || fresh.projectId !== projectId) {
        skipped.push(v.id)
        continue
      }
      const taskId = feedbackTaskId(v.id)
      if (!(await readOrNull(store, 'task', taskId)))
        await store.write('task', taskId, feedbackTaskRecord(v.id, fresh, 'user', Date.now()))
      await writeDecision(store, { projectId, proposalId: v.id, action: 'approve', taskId })
    } else {
      await writeDecision(store, { projectId, proposalId: v.id, action: 'reject' })
    }
    decided.push(v.id)
  }
  return { decided, skipped }
}
